import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../../data/monitoring.slice';
import { Box, Stack, styled } from '@mui/material';
import SelectMenu from '../../../forms/SelectMenu';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { SimplifiedWorkflowTriggerStepFormData } from '../../../../data/models/simplified-workflow.model';
import TextInput from '../../../forms/TextInput';
import {
  WaitActionTimePeriod,
  WaitAmountUnit,
} from '../../../../data/workflow-form.model';
import { lowerFirst } from 'lodash';
import { WorkflowBaseDateTimeRef } from '../../../../data/models/workflow.model';
import { useEffect } from 'react';

const PREFIX = 'TriggerStepForm';

const classes = {
  container: `${PREFIX}-container`,
  waitContainer: `${PREFIX}-waitContainer`,
  waitAmountTextInput: `${PREFIX}-waitAmountTextInput`,
  waitAmountUnitSelect: `${PREFIX}-waitAmountUnitSelect`,
  waitTimePeriodSelect: `${PREFIX}-waitTimePeriodSelect`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.waitContainer}`]: {
    marginTop: theme.spacing(3),
    gap: theme.spacing(1),
  },
  [`& .${classes.waitAmountTextInput}`]: {
    width: 120,
  },
  [`& .${classes.waitAmountUnitSelect}`]: {
    width: 140,
  },
  [`& .${classes.waitTimePeriodSelect}`]: {
    width: 140,
  },
}));

interface TriggerStepFormProps {
  handleFormUpdate: () => void;
}

export default function TriggerStepForm({
  handleFormUpdate,
}: TriggerStepFormProps) {
  const { t } = useTranslation('common');
  const { control, formState, setValue, getValues } =
    useFormContext<SimplifiedWorkflowTriggerStepFormData>();
  const rawSystemEvents = useAppSelector(monitoringSlice.selectSystemEvents);

  const systemEvents = [
    ...rawSystemEvents,
    { name: WorkflowBaseDateTimeRef.TripLegPickup, description: '' },
  ].sort((a, b) => a.name.localeCompare(b.name));
  const uniqueSystemEvents = systemEvents.filter(
    (event, index, self) =>
      self.findIndex((item) => item.name === event.name) === index,
  );
  const selectableSystemEvents = uniqueSystemEvents.map((event) => ({
    value: event.name,
    key: event.name,
  }));

  const getMinMaxForUnit = (unit: WaitAmountUnit) => {
    switch (unit) {
      case WaitAmountUnit.Days:
        return { min: 0, max: 99 };
      case WaitAmountUnit.Hours:
        return { min: 0, max: 24 };
      case WaitAmountUnit.Minutes:
        return { min: 0, max: 60 };
    }
  };

  const getValidateWaitRules = () => {
    const { wait } = getValues();
    if (wait) {
      const { waitAmountUnit } = wait;
      if (!waitAmountUnit) {
        return {};
      }
      const { min, max } = getMinMaxForUnit(waitAmountUnit);
      return {
        required: t('common.requiredField'),
        min: {
          value: min,
          message: t('workflows.rangeError', { min, max }),
        },
        max: {
          value: max,
          message: t('workflows.rangeError', { min, max }),
        },
      };
    }
    return {};
  };

  const waitAmountUnits = Object.values(WaitAmountUnit).map((value) => ({
    key: t(`workflows.${lowerFirst(value)}`),
    value,
  }));

  const event = useWatch({ control, name: 'event' });
  const wait = useWatch({ control, name: 'wait' });

  const getWaitActionTimePeriod = (event?: string) =>
    event === WorkflowBaseDateTimeRef.TripLegPickup
      ? WaitActionTimePeriod.Before
      : WaitActionTimePeriod.After;
  const rawWaitTimePeriods = [getWaitActionTimePeriod(event)];

  const waitTimePeriods = rawWaitTimePeriods.map((value) => ({
    key: t(`workflows.${value}`),
    value,
  }));

  useEffect(() => {
    if (!wait?.waitTimePeriod) {
      setValue('wait.waitTimePeriod', getWaitActionTimePeriod(event));
    }
  }, [event, wait?.waitTimePeriod]);

  useEffect(() => {
    if (event) {
      setValue('title', t('workflows.triggerWithEvent', { event }));
    }

    handleFormUpdate();
  }, [event, wait]);

  useEffect(() => {
    if (wait?.waitTimePeriod) {
      const valueStillExists = waitTimePeriods.some(
        (timePeriod) => timePeriod.value === wait.waitTimePeriod,
      );

      // Reset wait time period if no longer valid
      if (!valueStillExists) {
        setValue('wait.waitTimePeriod', undefined);
      }
    }
  }, [waitTimePeriods, wait]);

  return (
    <StyledBox>
      <SelectMenu
        name="event"
        label={t('workflows.trigger')}
        values={selectableSystemEvents}
        valueAttr="value"
        labelAttr="key"
        control={control}
        required
        errorMessage={formState.errors?.event?.message}
        rules={{ required: t('common.requiredField') }}
      />
      {event && (
        <Stack direction="row" className={classes.waitContainer}>
          <TextInput
            name="wait.waitAmount"
            label={t('workflows.amount')}
            className={classes.waitAmountTextInput}
            type="number"
            control={control}
            required
            rules={getValidateWaitRules()}
            errors={formState.errors}
          />
          <SelectMenu
            name="wait.waitAmountUnit"
            label={t('workflows.unit')}
            className={classes.waitAmountUnitSelect}
            values={waitAmountUnits}
            valueAttr="value"
            labelAttr="key"
            control={control}
            required
            errorMessage={
              formState.errors?.wait?.waitAmountUnit?.message as string
            }
            rules={{ required: t('common.requiredField') }}
          />
          <SelectMenu
            name="wait.waitTimePeriod"
            label={t('workflows.when')}
            className={classes.waitTimePeriodSelect}
            values={waitTimePeriods}
            valueAttr="value"
            labelAttr="key"
            control={control}
            required
            errorMessage={
              formState.errors?.wait?.waitTimePeriod?.message as string
            }
            rules={{ required: t('common.requiredField') }}
          />
        </Stack>
      )}
    </StyledBox>
  );
}
